import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMedicationChangeRequests, processMedicationChangeRequest } from "../../api/services/clinic/treatment";
import Badge from "../../components/Badge";
import Icon from "../../components/Icon";
import { useToast } from "../../contexts/ToastContext";
import realtimeClient from "../../realtime/client";
import ManageMedicationChangeRequestModal from "./ManageMedicationChangeRequestModal";
import ProcessMedicationChangeRequestModal from "./ProcessMedicationChangeRequestModal";
import type { MedicationChangeRequest as ProcessMedicationChangeRequest } from "./ProcessMedicationChangeRequestModal";

interface MedicationChangeRequest extends Omit<ProcessMedicationChangeRequest, "id"> {
    id: number;
    [key: string]: unknown;
}

interface Pagination {
    current_page?: number | null;
    total_pages?: number | null;
    [key: string]: unknown;
}

interface RequestListResponse extends Pagination {
    results?: MedicationChangeRequest[];
    count?: number | null;
}

interface RequestCountsPayload {
    data?: {
        medication_request_change_count?: number | null;
    };
}

interface ProcessRequestPayload {
    [key: string]: unknown;
}

interface FetchRequestOptions {
    status: "pending" | "completed";
    page: number;
    pageSize: number;
}

const fetchRequests = fetchMedicationChangeRequests as unknown as (
    options: FetchRequestOptions,
) => Promise<RequestListResponse>;

export default function MedicationRequestChangeBadge() {
    const [counts, setCounts] = useState(0);
    const [selectedRequestChange, setSelectedRequestChange] = useState<MedicationChangeRequest | null>(null);
    const [isManageMedicationChangeRequestModalOpen, setIsManageMedicationChangeRequestModalOpen] = useState(false);
    const [isProcessMedicationChangeRequestModalOpen, setIsProcessMedicationChangeRequestModalOpen] = useState(false);
    const [pendingRequests, setPendingRequests] = useState<MedicationChangeRequest[]>([]);
    const [completedRequests, setCompletedRequests] = useState<MedicationChangeRequest[]>([]);
    const [pendingPagination, setPendingPagination] = useState<Pagination | null>(null);
    const [completedPagination, setCompletedPagination] = useState<Pagination | null>(null);
    const [isPendingLoading, setIsPendingLoading] = useState(false);
    const [isCompletedLoading, setIsCompletedLoading] = useState(false);
    const [hasReceivedCounts, setHasReceivedCounts] = useState(false);
    const previousCountsRef = useRef(0);
    const pendingLoadingRef = useRef(false);
    const completedLoadingRef = useRef(false);

    const { showSuccessToast, showErrorToast } = useToast();

    const hasRequests = counts > 0;


    const handleMedicationRequestChangeCounts = useCallback((payload: unknown = {}) => {
        const data = (payload as RequestCountsPayload).data || {};
        setCounts(data.medication_request_change_count ?? 0);
    }, []);

    useEffect(() => {
        if (!hasReceivedCounts) {
            setHasReceivedCounts(true);
            if (counts > 0) {
                showSuccessToast("เจ้าหน้าที่ส่งคำร้องแก้ไขยาใหม่");
            }
            previousCountsRef.current = counts;
            return;
        }

        if (counts > previousCountsRef.current) {
            showSuccessToast("เจ้าหน้าที่ส่งคำร้องแก้ไขยาใหม่");
        }
        previousCountsRef.current = counts;
    }, [counts, hasReceivedCounts, showSuccessToast]);

    async function handleProcessMedicationChangeRequest(data: ProcessRequestPayload) {
        if (!selectedRequestChange) return;
        const medicationReviewRequestId = selectedRequestChange.id;
        const payload = data;
        try {
            await processMedicationChangeRequest(medicationReviewRequestId, payload);
            showSuccessToast("บันทึกการแก้ไขคำร้องสำเร็จ");
            setSelectedRequestChange(null);
            setIsProcessMedicationChangeRequestModalOpen(false);
        } catch (error) {
            showErrorToast("ไม่สามารถบันทึกการแก้ไขคำร้อง", error);
        }
    }


    useEffect(() => {
        const unsubscribe = realtimeClient.subscribe('medication_change_requested', handleMedicationRequestChangeCounts);
        return () => {
            unsubscribe();
        };
    }, [handleMedicationRequestChangeCounts]);

    async function handleOpenManageModal() {
        setIsManageMedicationChangeRequestModalOpen(true);
        await Promise.all([
            handleFetchRequests('pending'),
            handleFetchRequests('completed')
        ]);
    }

    async function handleFetchRequests(status: "pending" | "completed", page = 1, append = false): Promise<void> {
        const isPending = status === 'pending';
        if (isPending && pendingLoadingRef.current) return;
        if (!isPending && completedLoadingRef.current) return;

        if (isPending) {
            pendingLoadingRef.current = true;
            setIsPendingLoading(true);
        } else {
            completedLoadingRef.current = true;
            setIsCompletedLoading(true);
        }

        try {
            const response = await fetchRequests({ status, page, pageSize: 10 });
            const results = response.results || [];
            const currentPage = Number(response.current_page ?? page) || 1;
            const totalPagesFromCount = response.count
                ? Math.max(1, Math.ceil(response.count / 10))
                : 1;
            const totalPages = Number(response.total_pages ?? totalPagesFromCount) || 1;
            const normalizedPagination = {
                ...response,
                current_page: currentPage,
                total_pages: totalPages,
            };

            if (isPending) {
                setPendingRequests((prev) => append ? [...prev, ...results] : results);
                setPendingPagination(normalizedPagination);
            } else {
                setCompletedRequests((prev) => append ? [...prev, ...results] : results);
                setCompletedPagination(normalizedPagination);
            }
        } catch (error) {
            showErrorToast("ไม่สามารถแสดงคำร้องแก้ไขยาได้", error);
        } finally {
            if (isPending) {
                pendingLoadingRef.current = false;
                setIsPendingLoading(false);
            } else {
                completedLoadingRef.current = false;
                setIsCompletedLoading(false);
            }
        }
    }

    async function handleLoadMoreRequests(status: "pending" | "completed"): Promise<void> {
        const isPending = status === 'pending';
        const pagination = isPending ? pendingPagination : completedPagination;
        const isLoading = isPending ? pendingLoadingRef.current : completedLoadingRef.current;
        const currentPage = Number(pagination?.current_page ?? 1);
        const totalPages = Number(pagination?.total_pages ?? 1);

        if (isLoading || currentPage >= totalPages) {
            return;
        }

        await handleFetchRequests(status, currentPage + 1, true);
    }


    function buildIcon() {
        const className = hasRequests ? "text-creation-secondary-solid-on-surface-focusing" : "text-creation-neutral-container-on-surface-default";
        return (<Icon name="exclamation-circle" size="lg" className={className} />);
    }

    function buildLabel() {
        const className = `text-body-base ${hasRequests ? 'text-main-typography-system-inverted' : 'text-main-typography-system-default'}`;

        return (
            <div className="pl-2 pr-5">
                <span className={className}>คำร้องแก้ไขยา</span>
            </div>
        );
    }

    function buildBadge() {
        const text = `  ${counts}  `;
        if (hasRequests) {
            return (<Badge text={text} size="md" color="vivid" />);
        }
        return (<Badge text={text} size="md" color="oslo" />);
    }
    const className = `flex items-center rounded-lg px-4 py-3 ${hasRequests ? 'bg-creation-secondary-solid-surface-focusing' : 'bg-main-background-system-default border border-main-border-system-default'}`;

    return (
        <>
            <div className="fixed top-6 right-6 z-40">
                <button type="button" className={className} onClick={handleOpenManageModal}>
                    {buildIcon()}
                    {buildLabel()}
                    {buildBadge()}
                </button>
            </div>
            <ManageMedicationChangeRequestModal
                isOpen={isManageMedicationChangeRequestModalOpen}
                pendingRequestChanges={pendingRequests}
                completedRequestChanges={completedRequests}
                pendingPagination={pendingPagination}
                completedPagination={completedPagination}
                isPendingLoading={isPendingLoading}
                isCompletedLoading={isCompletedLoading}
                onLoadMorePending={() => handleLoadMoreRequests('pending')}
                onLoadMoreCompleted={() => handleLoadMoreRequests('completed')}
                onClose={() => {
                    setIsManageMedicationChangeRequestModalOpen(false);
                    setSelectedRequestChange(null);
                }}
                onProcessRequest={(requestChange) => {
                    setSelectedRequestChange(requestChange);
                    setIsManageMedicationChangeRequestModalOpen(false);
                    setIsProcessMedicationChangeRequestModalOpen(true);
                }}
            />
            <ProcessMedicationChangeRequestModal
                isOpen={isProcessMedicationChangeRequestModalOpen}
                onClose={() => {
                    setSelectedRequestChange(null);
                    setIsProcessMedicationChangeRequestModalOpen(false);
                    setIsManageMedicationChangeRequestModalOpen(true);
                }}
                requestChange={selectedRequestChange ?? {}}
                onSubmit={handleProcessMedicationChangeRequest}
            />
        </>
    )
}
